import { useRouter } from 'next/navigation';
import { CopyKind } from '@/components/AppBar/SelectPopover/copyKind';
import { useGlobalState } from '@/global-state/useGlobalState';
import useMutationWithFeedback from '@/hooks/utils/useMutationWithFeedback';
import { isTransportError } from '@/utils/network/isTransportError';
import { showError } from '@/utils/toast';
import { trpc } from '@/utils/trpc';
import { useBackgroundRefetch } from './useNetworks';
function reportRedirectViewLookupFailure(error: Error): void {
  console.error('Failed to resolve the duplicated view', error);
  showError('Opened the copy without a matching view', {
    description: error.message || 'Unexpected error'
  });
}
const RECOVERY_ATTEMPTS = 3;
const RECOVERY_RETRY_DELAY_MS = 2000;
export const useDuplicateNetwork = () => {
  const handleNetworkChange = useGlobalState(state => state.handleNetworkChange);
  const refetchInBackground = useBackgroundRefetch();
  const router = useRouter();
  const utils = trpc.useUtils();
  // suppressGlobalToast: the transport-error recovery below handles messaging
  // itself, so the inner mutation's React Query failure must not also fire the
  // global "Action failed" toast (KBE-738). Mirrors useImportNetwork.
  const duplicateNetworkMutation = trpc.network.duplicateNetwork.useMutation({
    meta: {
      suppressGlobalToast: true
    }
  });

  // KBE-738: a network duplicate is a long, heavy write. If the HTTP response
  // is lost (timeout / dropped connection) the server transaction can still
  // have committed, leaving the copy created while the client sees only a
  // transport error. We send a per-request idempotency key and, on a transport
  // failure, recover the copy BY THAT KEY — unambiguous (never resolves to a
  // pre-existing same-named network) — instead of reporting an outright failure
  // that hides the copy. The server deduplicates on the same key, so a retry
  // can't create a second copy.
  //
  // The commit can still be in flight when the response is lost, so poll a few
  // times before giving up. staleTime: 0 because the app-wide query client uses
  // staleTime: Infinity (a first miss would otherwise be cached forever).
  const recoverDuplicatedNetworkId = async (clientRequestId: string): Promise<string | undefined> => {
    for (let attempt = 0; attempt < RECOVERY_ATTEMPTS; attempt++) {
      // A failed lookup must not abort the remaining attempts: the connection
      // that just lost the duplicate response is the one most likely to drop
      // this read too, and the commit may still be in flight. Swallowing it
      // spends the retries the loop exists for. `meta.silent` keeps the global
      // QueryCache handler from stacking a "Failed to load data" toast beside
      // the duplicate one.
      const recoveredId = await utils.network.getNetworkIdByClientRequestId.fetch({
        clientRequestId
      }, {
        staleTime: 0,
        meta: {
          silent: true
        }
      }).catch(() => undefined);
      if (recoveredId) return recoveredId;
      if (attempt < RECOVERY_ATTEMPTS - 1) {
        await new Promise(resolve => setTimeout(resolve, RECOVERY_RETRY_DELAY_MS));
      }
    }
    return undefined;
  };
  const createRefBranchMutation = trpc.network.createRefBranch.useMutation();
  const createDeltaBranchMutation = trpc.network.createDeltaBranch.useMutation();
  const mutation = useMutationWithFeedback({
    mutation: async ({
      id,
      name,
      redirectToView,
      isReleaseBranch,
      copyKind = 'copy'
    }: {
      id: string;
      name: string;
      redirectToView: string | undefined;
      isReleaseBranch?: boolean;
      /** What the researcher picked in the Duplicate dialog. Defaults to a deep
       * copy for every source, the master and release branches included: a ref
       * branch re-bases onto its parent's head after a merge, so merging from it
       * again can revert changes others made to the parent meanwhile (KBE-981). */
      copyKind?: CopyKind;
    }) => {
      // The delta stores stub rows resolved against the source, not a copy. The
      // server rejects nesting (a delta branch off another delta branch).
      if (copyKind === 'delta') {
        const {
          networkId
        } = await createDeltaBranchMutation.mutateAsync({
          sourceNetworkId: id,
          newNetworkName: name
        });
        return {
          newId: networkId,
          newViewId: null
        };
      }
      if (copyKind === 'ref') {
        // The dialog hides the release-branch choice while a ref branch is
        // selected: a release branch accumulates patches and gets merged into,
        // so it must be a real copy.
        if (isReleaseBranch) {
          throw new Error('A release branch cannot be created as a ref branch; the Duplicate dialog should have cleared one of the two.');
        }
        const {
          networkId: networkId_0
        } = await createRefBranchMutation.mutateAsync({
          sourceNetworkId: id,
          newNetworkName: name
        });
        return {
          newId: networkId_0,
          newViewId: null
        };
      }
      const clientRequestId_0 = crypto.randomUUID();
      let newId: string;
      try {
        newId = (await duplicateNetworkMutation.mutateAsync({
          originalNetworkId: id,
          newNetworkName: name,
          isReleaseBranch: isReleaseBranch ?? false,
          clientRequestId: clientRequestId_0
        })).newNetworkId;
      } catch (error) {
        if (!isTransportError(error)) throw error;
        const recoveredId_0 = await recoverDuplicatedNetworkId(clientRequestId_0);
        if (!recoveredId_0) throw error;
        newId = recoveredId_0;
      }

      // A view with no counterpart in the copy is not an error -- the lookup
      // returns null for that. Anything that still throws here is a genuine
      // failure and stays visible; `meta.onError` only replaces the generic
      // "Failed to load data" wording, which reads as if the duplication
      // itself failed. See KBE-777.
      const newViewId = redirectToView ? await utils.view.getDuplicatedViewId.fetch({
        originalViewId: redirectToView,
        duplicatedNetworkId: newId
      }, {
        meta: {
          onError: reportRedirectViewLookupFailure
        }
      }).catch(() => null) : null;
      return {
        newId,
        newViewId
      };
    },
    success: 'Duplicated network!',
    error: 'Failed to duplicate network!',
    // Land on the duplicate first; refresh the lists behind it. See
    // `useBackgroundRefetch` for why awaiting the refresh here could strand
    // the researcher on the network they duplicated from.
    onSuccess: ({
      newId: newId_0,
      newViewId: newViewId_0
    }) => {
      handleNetworkChange();
      if (newViewId_0) {
        router.push(`/${newId_0}/${newViewId_0}`);
      } else {
        router.push(`/${newId_0}`);
      }
      refetchInBackground();
    }
  });
  return {
    ...mutation
  };
};